import { TimesheetEditor } from './TimesheetEditor';
import { useEffect, useState, type ReactNode } from "react";
import { summarizeJobTime, type JobTimeEntry } from "./job-record-model";
import { formatDispatchDate, formatDispatchTime } from "../lib/dispatch";

export type JobVisitTimes = {
  id: string;
  status?: string;
  technicianNames: string | null;
  dispatchedAt?: string | null;
  enRouteAt?: string | null;
  arrivedAt?: string | null;
  departedAt?: string | null;
};

const activeStatuses = ["dispatched", "en_route", "arrived", "in_progress"];
export function visitMinutes(visit: JobVisitTimes, now: number): number | null {
  if (!visit.dispatchedAt) return null;
  const start = Date.parse(visit.dispatchedAt);
  const end = visit.departedAt ? Date.parse(visit.departedAt) : activeStatuses.includes(visit.status ?? "") ? now : NaN;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  return Math.floor((end - start) / 60000);
}

export function JobVisitTimeline({ visit, timezone, summaryOnly = false }: { visit: JobVisitTimes; timezone: string; summaryOnly?: boolean }) {
  const [now, setNow] = useState(Date.now);
  const running = Boolean(visit.dispatchedAt && !visit.departedAt && activeStatuses.includes(visit.status ?? ""));
  useEffect(() => {
    if (!running) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, [running]);
  const minutes = visitMinutes(visit, now);
  const marks = [["Dispatched", visit.dispatchedAt], ["Arrived", visit.arrivedAt], ["Completed", visit.departedAt]].filter(([, value]) => value);
  const days = new Set(marks.map(([, value]) => formatDispatchDate(value!, timezone)));
  if (summaryOnly) return visit.departedAt && minutes !== null ? <p className="job-completed-duration" title="Total visit time includes travel from dispatch to completion.">{Math.floor(minutes / 60)}h {minutes % 60}m total time · includes travel</p> : null;
  if (!marks.length) return null;
  return <div aria-label="Visit timeline" className="job-visit-timeline">
    {(visit.technicianNames?.split(",").filter((name) => name.trim()).length ?? 0) > 1 && <span className="job-shared-time-label">Shared visit times</span>}
    {marks.map(([label, value], index) => <span key={label} className="inline-flex items-center gap-2">
      {index > 0 && <span aria-hidden="true">→</span>}
      <span className="job-clock-time">{label} <strong className="text-[var(--ink)]">{days.size > 1 ? `${formatDispatchDate(value!, timezone)} ` : ""}{formatDispatchTime(value!, timezone)}</strong></span>
    </span>)}
    {minutes !== null && <strong className="job-live-state">{Math.floor(minutes / 60)}h {minutes % 60}m visit elapsed{running ? " · running" : ""}</strong>}
  </div>;
}

export function JobTimesheets({ entries, timezone, title = "Technician time", emptyText = "No time recorded for this visit.", assignedNames, hideHeading = false, collapseTimeDetails = false, hideTimeDetails = false, renderProgress, canEdit = false, closed = false, onSaved = () => {}, onEditTime, payrollOnly = false }: { payrollOnly?: boolean; onEditTime?: (entry: JobTimeEntry) => void; entries: JobTimeEntry[]; timezone: string; canEdit?: boolean; closed?: boolean; onSaved?: () => void | Promise<void>; title?: string; emptyText?: string; assignedNames?: string | null; hideHeading?: boolean; collapseTimeDetails?: boolean; hideTimeDetails?: boolean; renderProgress?: (person: ReturnType<typeof summarizeJobTime>[number]) => ReactNode }) {
  const [now, setNow] = useState(Date.now);
  const [editing, setEditing] = useState<JobTimeEntry | null>(null);
  const running = !closed && entries.some((entry) => !entry.clockOut);
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, [running]);
  const people = summarizeJobTime(entries, now);
  if (closed) for (const person of people) {
    person.minutes = summarizeJobTime(person.entries.filter(entry => entry.clockOut), now).reduce((total, item) => total + item.minutes, 0);
    person.running = false;
  }
  const normalize = (name: string) => name.trim().replace(/\s+/g, " ").toLowerCase();
  for (const name of assignedNames?.split(",").map((name) => name.trim()).filter(Boolean) ?? []) {
    if (!people.some((person) => normalize(person.name) === normalize(name))) people.push({ id: `assigned-${normalize(name)}`, name, minutes: 0, running: false, entries: [] });
  }
  return <section aria-label={title} className="job-visit-timesheets">
    {!hideHeading && <h3 className="job-muted">{title}</h3>}
    {!people.length && !hideHeading && <p className="job-muted">{emptyText}</p>}
    <div className="divide-y divide-[var(--line)]">{people.map((person) => {
      const timeDetails = <>
      {person.entries.map((entry) => <span key={entry.id} className="inline-flex flex-wrap items-center gap-2 text-[var(--ink-soft)]">
        <span className="job-time-date">{formatDispatchDate(entry.clockIn, timezone)}</span>
        <span className="job-clock-time">Start <strong>{formatDispatchTime(entry.clockIn, timezone)}</strong></span>
        <span aria-hidden="true">→</span>
        <span className="job-clock-time">End <strong>{entry.clockOut ? `${formatDispatchDate(entry.clockOut, timezone) !== formatDispatchDate(entry.clockIn, timezone) ? `${formatDispatchDate(entry.clockOut, timezone)} ` : ""}${formatDispatchTime(entry.clockOut, timezone)}` : closed ? "Missing end time" : "Running"}</strong></span>
        {canEdit && <button type="button" onClick={() => onEditTime ? onEditTime(entry) : setEditing(entry)} className="rounded-lg border border-[var(--line)] px-3 py-2 text-sm font-semibold" aria-label={`Edit time for ${person.name}`}>Edit time</button>}
      </span>)}
      {closed && person.entries.some(entry => !entry.clockOut) && <span className="text-sm text-amber-700">Time needs correction</span>}
      {person.entries.some(entry => entry.clockOut || !closed) && <strong className="job-live-state">{Math.floor(person.minutes / 60)}h {person.minutes % 60}m recorded</strong>}
      </>;
      const progress = renderProgress?.(person);
      return <div key={person.id} className="job-technician-time-row">
      <strong>{person.name}</strong>
      {progress}
      {hideTimeDetails && progress ? null : collapseTimeDetails ? person.entries.length > 0 && <details className="job-technician-time-details"><summary>Time records</summary><div>{timeDetails}</div></details> : timeDetails}
    </div>; })}</div>
    {editing && <TimesheetEditor payrollOnly={payrollOnly} entry={editing} timezone={timezone} onClose={() => setEditing(null)} onSaved={onSaved} />}
  </section>;
}
